/**
 * The Audit Insights body for an audit that is not complete.
 *
 * Reached whenever the read API reports NOT_APPLICABLE, which it does for every audit
 * status other than completed — so this one view serves an audit still to be performed, one
 * part-way through, and one that expired or was cancelled without ever finishing. Consumers
 * pass the audit's own status so the banner can say "not yet" or "never"; everything below
 * the banner reads the same either way.
 */
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Chip, ChipBackgroundColor, ChipSize } from '@amzn/stencil-react-components/chip';
import {
  Table,
  TableSpacing,
  type TableColumn,
} from '@amzn/stencil-react-components/table';
import { MessageBanner } from '@amzn/stencil-react-components/message-banner';
import { Pagination } from '@amzn/stencil-react-components/pagination';
import { IconGrowthSmall, IconNumberedListSmall } from '@amzn/stencil-react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocalPagination } from 'src/v2/shared/hooks';
import type { Messages } from 'src/v2/shared/i18n/useMessages';
import { htmlAsPlainText } from 'src/v2/shared/domain';
import type { AuditInsightsMessageKey } from '../translations';
import { SectionHeading } from './SectionHeading';
import { InsightTile, type TileTone } from './InsightTile';
import { incompleteReason } from './incompleteReason';
import { DETAIL_TONE, resultReading, type DetailTone } from '../logic/insightDetail';
import {
  QUESTION_FILTER,
  filterQuestions,
  questionTally,
  type QuestionFilter,
} from './questionFilter';

/**
 * Rows per page in the question list.
 *
 * Matches the Summary tab's question table, so a reader moving between the two tabs pages
 * through the same audit in the same steps.
 */
const QUESTIONS_PER_PAGE = 20;

/** A question as this view needs it, resolved to the reader's language by the caller. */
export interface InsightQuestion {
  questionId: string;
  title: string;
  questionText: string;
  points: number;
  /**
   * The auditor's result, absent until the question is answered. Held as a string because
   * Helios owns this vocabulary and may add to it.
   */
  result?: string;
}

/** Pass/fail/NA counts as the audit itself reports them. Never recomputed here. */
export interface ResponseBreakdown {
  passed: number;
  failed: number;
  na: number;
  total: number;
}

interface IncompleteAuditInsightsProps {
  breakdown: ResponseBreakdown;
  questions: InsightQuestion[];
  /**
   * The audit's live status, which decides what the banner says. Absent while the summary
   * loads, and the banner then says only what holds for any incomplete audit.
   */
  auditStatus?: string;
  messages: Messages<AuditInsightsMessageKey>;
}

/**
 * What the tab shows for an audit that is not complete: why the sections are absent, the
 * response counts so far, and the questions the audit asks.
 *
 * Reached for every audit status other than completed — one still to be performed, one
 * part-way through, and one that expired or was cancelled without ever finishing. The
 * banner tells them apart; everything below it reads the same either way.
 */
export function IncompleteAuditInsights({
  breakdown,
  questions,
  auditStatus,
  messages,
}: IncompleteAuditInsightsProps): JSX.Element {
  const { t, f } = messages;
  const reason = incompleteReason(auditStatus);

  // Total to begin with: the reader has not asked a narrower question yet.
  const [filter, setFilter] = useState<QuestionFilter>(QUESTION_FILTER.total);
  const visible = useMemo(() => filterQuestions(questions, filter), [questions, filter]);
  // Reports the whole selection, not the page, so the heading answers "how many passed"
  // rather than "how many are on screen".
  const tally = useMemo(() => questionTally(visible), [visible]);
  const { currentPageIndex, setCurrentPageIndex, paginatedItems, totalPages } =
    useLocalPagination({ items: visible, pageSize: QUESTIONS_PER_PAGE });

  const selectFilter = useCallback(
    (next: QuestionFilter) => {
      setFilter(next);
      // A narrower selection can have fewer pages than the one on screen, which would
      // otherwise strand the reader on a page past the end of the new list.
      setCurrentPageIndex(1);
    },
    [setCurrentPageIndex],
  );

  // The list can also shrink without the reader touching the filter — a refreshed summary
  // returns fewer questions — and the pagination hook keeps the page it was left on. Past
  // the end, the table draws empty and the pager is withheld, leaving no control to return
  // with. Held here rather than in the hook, which nine legacy pages also call.
  useEffect(() => {
    if (totalPages > 0 && currentPageIndex > totalPages) {
      setCurrentPageIndex(totalPages);
    }
  }, [currentPageIndex, totalPages, setCurrentPageIndex]);

  // Keyed on `t`, which `useMessages` holds stable, rather than on the bundle it is read
  // from: that is returned by value, so a new object arrives on every render of the host.
  const StatusCell = useMemo(() => statusCell(t), [t]);

  // Read out here rather than inside the memo below, so the memo depends on the heading
  // strings themselves.
  //
  // A heading is a string held in the column, not a function the table calls per render, and
  // `t` keeps one identity for the whole life of the component while answering with the key
  // until the bundle arrives. A memo keyed on `t` alone therefore holds the four keys it was
  // first built with and never picks up the translations. Depending on the values fixes that
  // and re-reads on a locale change too, which no flag on the bundle would catch.
  const headingQuestionId = t('columnQuestionId');
  const headingQuestion = t('columnQuestion');
  const headingPoints = t('columnPoints');
  const headingStatus = t('columnStatus');

  // Held across renders because Stencil's row memoises its expandable-row measurement
  // against this array's identity: a fresh one each render redoes that work on every row.
  const columns: TableColumn<InsightQuestion>[] = useMemo(
    () => [
      {
        header: headingQuestionId,
        // Prefixed, as every other question reference in this tab is. Helios stores the id
        // bare, so rendering the raw value would print `10` where the rest of the page and
        // the audit itself say `Q10`.
        accessor: ({ data }) => f('questionIdChip', { id: data.questionId }),
        width: '15%',
      },
      { header: headingQuestion, cellComponent: QuestionCell },
      {
        header: headingPoints,
        // Carries its unit, as the prototype's cell does. The heading tally above counts
        // points too, so a bare figure here reads as a quantity of something unnamed.
        accessor: ({ data }) => f('pointsValue', { count: data.points }),
        textAlign: 'center',
        width: '12%',
      },
      {
        header: headingStatus,
        cellComponent: StatusCell,
        width: '15%',
      },
    ],
    [f, StatusCell, headingQuestionId, headingQuestion, headingPoints, headingStatus],
  );

  return (
    <Col gridGap="dimensions.spacing.400">
      <MessageBanner
        type={reason.type}
        isDismissible={false}
        titleText={t(reason.titleKey)}
      >
        {/* Formatted with the count throughout, though only the not-started wording spends
            it. Keeping the call uniform means a translator can reach for the figure in any
            of these without a change here. */}
        {f(reason.bodyKey, { count: breakdown.total })}
      </MessageBanner>

      <Col gridGap="dimensions.spacing.200">
        <SectionHeading icon={<IconGrowthSmall />} label={t('breakdownTitle')} />
        <Row gridGap="dimensions.spacing.300" flexWrap="wrap">
          {breakdownTiles(breakdown, t).map((tile) => (
            <InsightTile
              key={tile.filter}
              count={tile.count}
              label={tile.label}
              {...(tile.tone ? { tone: tile.tone } : {})}
              isSelected={filter === tile.filter}
              selects="filter"
              onClick={() => selectFilter(tile.filter)}
            />
          ))}
        </Row>
      </Col>

      {questions.length > 0 ? (
        <Col gridGap="dimensions.spacing.200" minWidth={0}>
          <SectionHeading
            icon={<IconNumberedListSmall />}
            label={t('questionsTitle')}
            // The tally counts what the list shows, not the whole audit, so selecting a
            // count and reading the tally agree with each other.
            badge={f('questionsContext', tally)}
            badgeAlign="end"
          />
          {visible.length > 0 ? (
            <>
              <Table
                columns={columns}
                data={paginatedItems}
                // Annotated because Stencil declares Table as FC<TableProps<any, any>>, so the
                // row type is not inferred from `data`.
                rowKeyAccessor={(question: InsightQuestion) => question.questionId}
                spacing={TableSpacing.Reduced}
              />
              {/* Withheld on a single page: a pager offering only page one is chrome that
                  cannot be acted on. */}
              {totalPages > 1 ? (
                <Row justifyContent="center">
                  <Pagination
                    aria-label={t('questionsPaginationLabel')}
                    numberOfPages={totalPages}
                    selectedPage={currentPageIndex}
                    onPageSelect={setCurrentPageIndex}
                  />
                </Row>
              ) : null}
            </>
          ) : (
            // The audit has questions, just none with this result. Said plainly, so the
            // reader knows the filter worked rather than wondering if the list broke.
            <Col
              backgroundColor="color.surface.bg-muted"
              borderRadius="dimensions.border.radius.200"
              padding="dimensions.spacing.300"
            >
              <Text fontSize="T200" color="color.text.secondary">
                {t('questionsNoneMatch')}
              </Text>
            </Col>
          )}
        </Col>
      ) : null}
    </Col>
  );
}

/** One selectable count above the question list. */
interface BreakdownTile {
  filter: QuestionFilter;
  label: string;
  count: number;
  /** Absent for the total, which is neither good nor bad news. */
  tone?: TileTone;
}

/**
 * The four counts in reading order: the two verdicts, then what was skipped, then the total.
 *
 * Counts come from the audit's own metrics and are never recomputed from the questions on
 * hand. Selecting one narrows the list below, and the list reports its own tally — so if
 * the audit's count and the questions it returned ever disagree, the reader sees both
 * rather than a silently corrected figure.
 */
function breakdownTiles(
  breakdown: ResponseBreakdown,
  t: (key: AuditInsightsMessageKey) => string,
): BreakdownTile[] {
  return [
    {
      filter: QUESTION_FILTER.pass,
      label: t('breakdownPass'),
      count: breakdown.passed,
      tone: 'positive',
    },
    {
      filter: QUESTION_FILTER.fail,
      label: t('breakdownFail'),
      count: breakdown.failed,
      tone: 'critical',
    },
    // Muted: not answering a question is neither good nor bad news, and this reads as an
    // aside to the three counts that are.
    {
      filter: QUESTION_FILTER.na,
      label: t('breakdownNa'),
      count: breakdown.na,
      tone: 'muted',
    },
    // No tone, so the total takes the default text colour: it is the figure the other three
    // add up to, so it anchors the row rather than commenting on it.
    {
      filter: QUESTION_FILTER.total,
      label: t('breakdownTotal'),
      count: breakdown.total,
    },
  ];
}

/** Title over the question's own wording, as the prototype stacks them. */
function QuestionCell({ data }: { data: InsightQuestion }): JSX.Element {
  return (
    <Col gridGap="dimensions.spacing.50" minWidth={0}>
      <Text fontSize="T200">{data.title}</Text>
      <Text fontSize="T100" color="color.text.secondary">
        {/* Authored as rich text, so the raw value is HTML. A table row shows a string
            rather than rendering markup, and a cell is too small for a picture. */}
        {htmlAsPlainText(data.questionText)}
      </Text>
    </Col>
  );
}

/**
 * The chip tint each verdict gets. Pass reads green and fail red; a result carrying no
 * verdict — N/A, or a question nobody has answered — stays the default grey, matching the
 * counts above, where N/A and the total are the two that are neither good nor bad news.
 */
const RESULT_CHIP_COLOR = new Map<DetailTone, ChipBackgroundColor>([
  [DETAIL_TONE.positive, ChipBackgroundColor.Green],
  [DETAIL_TONE.critical, ChipBackgroundColor.Red],
]);

/**
 * The status cell needs a translator, which a bare cell component cannot reach, so it is
 * closed over here.
 *
 * Takes `t` rather than the whole bundle so the caller can hold the built cell across
 * renders: `useMessages` returns its bundle by value but keeps `t` itself stable.
 *
 * The recorded result is a Helios enum, not display text: printing it raw shows `PASS`
 * where the reader is owed `Pass`. A result this build does not know reads as unanswered
 * rather than as the raw value.
 */
function statusCell(t: Messages<AuditInsightsMessageKey>['t']) {
  return function StatusCell({ data }: { data: InsightQuestion }): JSX.Element {
    const reading = resultReading(data.result);
    const tint = reading?.tone ? RESULT_CHIP_COLOR.get(reading.tone) : undefined;

    return (
      <Chip
        size={ChipSize.ExtraSmall}
        backgroundColor={tint ?? ChipBackgroundColor.Default}
      >
        {reading ? t(reading.label) : t('statusPending')}
      </Chip>
    );
  };
}
